import React, { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
interface PlantProps {
  /** 0 → 1, derived from the timer: elapsed focus time / session duration. */
  progress: number;
  /** True while the timer is running — drives the gentle sway only. */
  running: boolean;
  /** Increments on each completed focus session to fire the one-shot flourish. */
  growthKey: number;
}
interface Leaf {
  id: string;
  /** Attachment point on the stem. */
  x: number;
  y: number;
  /** Degrees; ~195 points up-left, ~-20 points up-right. */
  rotate: number;
  scale: number;
  color: 'charcoal' | 'coral';
  /** Progress at which the leaf unfurls. */
  at: number;
}

/* A single leaf outline, drawn from its attachment point toward +x. */
const LEAF_PATH = 'M0 0C12 -11 28 -11 37 -1C28 10 11 11 0 0Z';
const LEAF_VEIN = 'M3 0C13 -1 24 0 32 -1';
const LEAVES: Leaf[] = [{
  id: 'sprout-l',
  x: 80,
  y: 120,
  rotate: 202,
  scale: 0.42,
  color: 'charcoal',
  at: 0
}, {
  id: 'sprout-r',
  x: 80,
  y: 118,
  rotate: -22,
  scale: 0.42,
  color: 'coral',
  at: 0
}, {
  id: 'leaf-1',
  x: 80,
  y: 106,
  rotate: 196,
  scale: 0.72,
  color: 'charcoal',
  at: 0.25
}, {
  id: 'leaf-2',
  x: 80,
  y: 93,
  rotate: -16,
  scale: 0.8,
  color: 'coral',
  at: 0.5
}, {
  id: 'leaf-3',
  x: 80,
  y: 79,
  rotate: 200,
  scale: 0.9,
  color: 'charcoal',
  at: 0.75
}, {
  id: 'crown',
  x: 80,
  y: 62,
  rotate: -34,
  scale: 0.95,
  color: 'coral',
  at: 0.999
}];
const EASE = [0.23, 1, 0.32, 1] as const;
export function Plant({
  progress,
  running,
  growthKey
}: PlantProps) {
  const reduceMotion = useReducedMotion();
  const [celebrating, setCelebrating] = useState(false);
  const lastGrowth = useRef(growthKey);
  useEffect(() => {
    if (growthKey === lastGrowth.current) return;
    lastGrowth.current = growthKey;
    if (reduceMotion) return;
    setCelebrating(true);
    const id = window.setTimeout(() => setCelebrating(false), 900);
    return () => window.clearTimeout(id);
  }, [growthKey, reduceMotion]);
  const t = Math.min(1, Math.max(0, progress));
  // Stem starts as a short sprout and extends with the session.
  const stemDraw = 0.22 + 0.78 * t;
  const plantScale = 0.62 + 0.38 * t;
  const sway = running && !reduceMotion;
  return <div className="mx-auto h-44 w-44" data-id="element-49" data-mp="34bjz69wq6uej">
      <svg viewBox="0 0 160 160" role="img" aria-label={`Plant illustration, ${Math.round(t * 100)} percent grown`} className="h-full w-full overflow-visible" data-id="element-50" data-mp="34clbsite3oyd">
        <ellipse cx="80" cy="140" rx="46" ry="7" className="text-line" fill="currentColor" data-id="element-51" data-mp="utnpfidjp5g" />

        {/* Scale and sway share one group anchored at the base of the stem,
            so growth never shifts the surrounding layout. */}
        <motion.g style={{
        transformBox: 'view-box',
        transformOrigin: '80px 132px'
      }} initial={false} animate={{
        scale: plantScale,
        rotate: sway ? [-1.1, 1.1, -1.1] : 0
      }} transition={{
        scale: {
          duration: reduceMotion ? 0 : 0.6,
          ease: EASE
        },
        rotate: sway ? {
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut'
        } : {
          duration: reduceMotion ? 0 : 0.3,
          ease: EASE
        }
      }} data-id="element-52" data-mp="3s8plfmz0ht9r">
          <motion.path d="M80 132C80 118 78 106 80 94C82 82 80 70 80 58" stroke="var(--leaf)" strokeWidth="4.5" strokeLinecap="round" fill="none" pathLength={1} strokeDasharray="1 1" initial={false} animate={{
          strokeDashoffset: 1 - stemDraw
        }} transition={{
          duration: reduceMotion ? 0 : 0.6,
          ease: EASE
        }} data-id="element-53" data-mp="248u7os02g5jg" />

            {LEAVES.map(leaf => {
          const open = t >= leaf.at;
          const isCrown = leaf.id === 'crown';
          const target = open ? leaf.scale : 0.001;
          return <g key={leaf.id} transform={`translate(${leaf.x} ${leaf.y}) rotate(${leaf.rotate})`} data-id="element-54" data-mp="3apfrk4gwh8ex">
                  <motion.g style={{
              transformBox: 'fill-box',
              transformOrigin: 'left center'
            }} initial={false} animate={{
              scale: isCrown && celebrating && open ? [leaf.scale * 0.7, leaf.scale * 1.14, leaf.scale] : target,
              opacity: open ? 1 : 0
            }} transition={{
              duration: reduceMotion ? 0 : isCrown && celebrating ? 0.7 : 0.45,
              ease: EASE
            }} data-id="element-55" data-mp="36ovtmyoi60rq">
                    <path d={LEAF_PATH} fill={leaf.color === 'coral' ? 'var(--coral)' : 'var(--leaf)'} data-id="element-56" data-mp="st43mkw6563s" />
                    <path d={LEAF_VEIN} stroke="var(--bg)" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.5" data-id="element-57" data-mp="xlx6t4bzo6dr" />
                  </motion.g>
                </g>;
        })}
        </motion.g>

        <path d="M54 132 A 26 15 0 0 0 106 132 Z" fill="var(--leaf)" data-id="element-58" data-mp="2x0yrbbqpxi7p" />
      </svg>
    </div>;
}